import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { importTiersExcel } from '../../reduxStore/TiersSlice';
import { TypeTiers } from '../../constants/enums';
import api from '../../reduxStore/api';

const PREVIEW_COLUMNS = ['nom', 'prenom', 'telephone', 'email', 'adresse', 'categorie_nom'];
const MAX_LIGNES = 500;

interface ImportResult {
  success: number;
  errors: Array<{ row: number; message: string }>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  tiersType: TypeTiers;   // imposé à toutes les lignes du fichier
  labelPluriel: string;   // labels.clientPluriel / labels.fournisseurPluriel
}

const ImportTiersDialog: React.FC<Props> = ({ open, onClose, onImported, tiersType, labelPluriel }) => {
  const dispatch = useDispatch<AppDispatch>();
  const inputRef = useRef<HTMLInputElement>(null);

  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const nomPluriel = labelPluriel.toLowerCase();

  const handleClose = () => {
    setRows([]);
    setFileName(null);
    setResult(null);
    setParseError(null);
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/tiers/template', { params: { type: tiersType }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `modele_${nomPluriel}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setResult(null);
    setParseError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { cellDates: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const parsed = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: null });
        const normalized = parsed
          .filter(row => Object.values(row).some(v => v !== null && v !== ''))
          .map(row => {
            const out: Record<string, any> = {};
            for (const [k, v] of Object.entries(row))
              out[k] = v instanceof Date ? v.toISOString().slice(0, 10) : v;
            return out;
          });

        if (normalized.length === 0) {
          setParseError(`Le fichier ne contient aucune ligne valide.`);
          setRows([]);
        } else if (normalized.length > MAX_LIGNES) {
          setParseError(`Le fichier contient ${normalized.length} lignes — maximum autorisé : ${MAX_LIGNES}.`);
          setRows([]);
        } else {
          setRows(normalized);
        }
      } catch {
        setParseError('Impossible de lire le fichier. Vérifiez qu\'il s\'agit d\'un fichier Excel (.xlsx).');
        setRows([]);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (rows.length === 0) return;
    setImporting(true);
    try {
      const action = await dispatch(importTiersExcel({ lignes: rows, type: tiersType }));
      if (importTiersExcel.fulfilled.match(action)) {
        setResult(action.payload);
        if (action.payload.errors.length === 0) onImported();
      } else {
        setResult({ success: 0, errors: [{ row: 0, message: String(action.payload ?? 'Erreur inconnue') }] });
      }
    } finally {
      setImporting(false);
    }
  };

  const previewCols = rows.length > 0
    ? PREVIEW_COLUMNS.filter(col => rows.some(r => r[col] != null && r[col] !== ''))
    : [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Importer des {nomPluriel} depuis Excel</DialogTitle>

      <DialogContent dividers>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
            Télécharger le modèle
          </Button>
          <Button variant="contained" size="small" startIcon={<UploadFileIcon />} onClick={() => inputRef.current?.click()}>
            {fileName ? 'Changer le fichier' : 'Choisir un fichier'}
          </Button>
          {fileName && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {fileName} — {rows.length} ligne{rows.length > 1 ? 's' : ''}
            </Typography>
          )}
          <input ref={inputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileChange} />
        </Box>

        {parseError && <Alert severity="error" sx={{ mb: 2 }}>{parseError}</Alert>}

        {result && result.errors.length === 0 && (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
            {result.success} ligne{result.success > 1 ? 's' : ''} importée{result.success > 1 ? 's' : ''} avec succès.
          </Alert>
        )}

        {result && result.errors.length > 0 && (
          <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              Aucune ligne importée — {result.errors.length} erreur{result.errors.length > 1 ? 's' : ''} détectée{result.errors.length > 1 ? 's' : ''} :
            </Typography>
            {result.errors.map((e) => (
              <Typography key={e.row} variant="body2">
                • {e.row > 0 ? `Ligne ${e.row} : ` : ''}{e.message}
              </Typography>
            ))}
          </Alert>
        )}

        {rows.length > 0 && result === null && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', display: 'block', mb: 1 }}>
              Aperçu — {rows.length} ligne{rows.length > 1 ? 's' : ''} à importer
            </Typography>
            <Paper variant="outlined" sx={{ overflow: 'auto', maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', bgcolor: 'grey.100' }}>#</TableCell>
                    {previewCols.map(col => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', bgcolor: 'grey.100', whiteSpace: 'nowrap' }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={idx} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>{idx + 2}</TableCell>
                      {previewCols.map(col => (
                        <TableCell key={col} sx={{ fontSize: '0.8rem', fontFamily: typeof row[col] === 'number' ? 'monospace' : undefined }}>
                          {row[col] != null && row[col] !== '' ? String(row[col]) : <span style={{ color: '#bbb' }}>—</span>}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>Fermer</Button>
        {result === null && (
          <Button
            variant="contained"
            disabled={rows.length === 0 || importing}
            onClick={handleImport}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {importing ? 'Import en cours…' : `Importer ${rows.length > 0 ? rows.length + ' ligne' + (rows.length > 1 ? 's' : '') : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportTiersDialog;
